import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Building2,
  Calendar,
  Download,
  HardHat,
  PiggyBank,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  User,
  Wallet,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassChart, FadeIn, StaggerContainer, StaggerItem } from '../../components/ui';
import { api } from '../../hooks/useApi';

const BOOKS = {
  imobiliaria: {
    name: 'Imobiliaria',
    color: '#22c55e',
    icon: Building2,
    bgColor: 'rgba(34, 197, 94, 0.1)',
  },
  construtora: {
    name: 'Construtora',
    color: '#3b82f6',
    icon: HardHat,
    bgColor: 'rgba(59, 130, 246, 0.1)',
  },
  pessoal: {
    name: 'Pessoal',
    color: '#8b5cf6',
    icon: User,
    bgColor: 'rgba(139, 92, 246, 0.1)',
  },
  geral: {
    name: 'Geral',
    color: '#f59e0b',
    icon: Wallet,
    bgColor: 'rgba(245, 158, 11, 0.1)',
  },
};

const emptyDashboard = {
  periodo: null,
  contas: { total: 0, saldo_atual: 0, items: [] },
  consolidado: {
    total_receitas: 0,
    total_despesas: 0,
    saldo_geral: 0,
    resultado: 0,
    a_receber: 0,
    a_pagar: 0,
    previsao_saldo: 0,
  },
  books: {},
  monthly: [],
  recentes: [],
  alertas: [],
};

const formatBRL = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR');
};

export const FinanceiroDashboard = () => {
  const [selectedBook, setSelectedBook] = useState('all');
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/contabilidade/dashboard');
      setDashboard({ ...emptyDashboard, ...response.data });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar financeiro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const visibleBooks = useMemo(() => {
    return Object.entries(BOOKS).map(([key, meta]) => ({
      key,
      meta,
      data: dashboard.books?.[key] || emptyDashboard.consolidado,
    }));
  }, [dashboard.books]);

  const chartKeys = selectedBook === 'all'
    ? [
        { key: 'receitas', name: 'Receitas' },
        { key: 'despesas', name: 'Despesas' },
        { key: 'resultado', name: 'Resultado' },
      ]
    : [{ key: selectedBook, name: BOOKS[selectedBook]?.name || selectedBook }];

  const c = dashboard.consolidado || emptyDashboard.consolidado;
  const abertas = c.abertas || {};
  const resultadoPositive = Number(c.resultado || 0) >= 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Financeiro</h1>
          <p className="text-white/50">Realizado no periodo, aberto total e previsao de caixa</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={`${process.env.PUBLIC_URL || ''}/manuals/playbook-humano-financeiro-construtora.md`}
            download
            className="relative inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-accent-primary/50 bg-white/10 text-white/90 border border-white/20 hover:bg-white/20 hover:border-white/30 px-5 py-2.5 text-sm"
          >
            <Download className="w-4 h-4" />
            Manual
          </a>
          <select
            value={selectedBook}
            onChange={(e) => setSelectedBook(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white/90"
          >
            <option value="all">Todos</option>
            {Object.entries(BOOKS).map(([key, book]) => (
              <option key={key} value={key}>{book.name}</option>
            ))}
          </select>
          <GlassButton icon={loading ? RefreshCw : Calendar} onClick={loadDashboard}>
            {loading ? 'Atualizando' : 'Atualizar'}
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10">
          <div className="flex items-center gap-2 text-red-300">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </GlassCard>
      )}

      <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {visibleBooks.map(({ key, meta, data }) => {
          const Icon = meta.icon;
          const isSelected = selectedBook === 'all' || selectedBook === key;
          const saldo = Number(data.saldo || 0);

          return (
            <StaggerItem key={key}>
              <GlassCard
                className={`p-6 cursor-pointer transition-all ${!isSelected ? 'opacity-50' : ''}`}
                style={{ borderColor: isSelected ? meta.color : undefined }}
                onClick={() => setSelectedBook(key === selectedBook ? 'all' : key)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: meta.bgColor }}>
                      <Icon className="w-6 h-6" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white/90">{meta.name}</h3>
                      <p className="text-xs text-white/50">{key === 'geral' ? 'Sem vinculo' : 'Classificado'}</p>
                    </div>
                  </div>
                  <div className="px-2 py-1 rounded-lg text-xs font-medium" style={{ backgroundColor: meta.bgColor, color: meta.color }}>
                    Real
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
	                    <span className="text-sm text-white/50">Resultado realizado</span>
                    <span className="text-xl font-bold" style={{ color: saldo >= 0 ? meta.color : '#ef4444' }}>
                      {formatBRL(saldo)}
                    </span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
	                      <p className="text-white/40">Receitas pagas</p>
                      <p className="text-green-400 font-medium">{formatBRL(data.receitas)}</p>
                    </div>
                    <div>
	                      <p className="text-white/40">Despesas pagas</p>
                      <p className="text-red-400 font-medium">{formatBRL(data.despesas)}</p>
                    </div>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex items-center justify-between text-sm">
	                    <span className="text-white/40">A receber aberto</span>
	                    <span className="text-white/70">{formatBRL(data.pendente_receita ?? data.pendente)}</span>
                  </div>
                </div>
              </GlassCard>
            </StaggerItem>
          );
        })}
      </StaggerContainer>

      <FadeIn delay={0.2}>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white/90 flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-accent-primary" />
	              Resumo Consolidado
            </h3>
            <span className="text-sm text-white/50">
	              {dashboard.periodo ? `Realizado: ${formatDate(dashboard.periodo.inicio)} - ${formatDate(dashboard.periodo.fim)}` : 'Periodo atual'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="p-4 rounded-xl bg-white/[0.02]">
	              <p className="text-sm text-white/50 mb-1">Receitas pagas no periodo</p>
              <p className="text-xl font-bold text-green-400">{formatBRL(c.total_receitas)}</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02]">
	              <p className="text-sm text-white/50 mb-1">Despesas pagas no periodo</p>
              <p className="text-xl font-bold text-red-400">{formatBRL(c.total_despesas)}</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02]">
              <p className="text-sm text-white/50 mb-1">Resultado</p>
              <p className={`text-xl font-bold ${resultadoPositive ? 'text-accent-primary' : 'text-red-400'}`}>{formatBRL(c.resultado)}</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02]">
	              <p className="text-sm text-white/50 mb-1">Saldo bancario cadastrado</p>
              <p className="text-xl font-bold text-white/90">{formatBRL(c.saldo_geral)}</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02]">
	              <p className="text-sm text-white/50 mb-1">A receber aberto total</p>
              <p className="text-xl font-bold text-yellow-400">{formatBRL(c.a_receber)}</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02]">
              <p className="text-sm text-white/50 mb-1">A Pagar</p>
              <p className="text-xl font-bold text-orange-400">{formatBRL(c.a_pagar)}</p>
            </div>
	          </div>
	          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
	            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
	              <p className="text-sm text-white/50 mb-1">Recebiveis vencidos</p>
	              <p className="text-lg font-bold text-red-300">{formatBRL(abertas.receitas_vencidas)}</p>
	            </div>
	            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
	              <p className="text-sm text-white/50 mb-1">Recebiveis proximos 90 dias</p>
	              <p className="text-lg font-bold text-blue-300">{formatBRL(abertas.receitas_a_vencer_periodo)}</p>
	            </div>
	            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
	              <p className="text-sm text-white/50 mb-1">Recebiveis futuros</p>
	              <p className="text-lg font-bold text-white/80">{formatBRL(abertas.receitas_futuras)}</p>
	            </div>
	          </div>
	        </GlassCard>
	      </FadeIn>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <FadeIn delay={0.3} className="xl:col-span-2">
          <GlassCard>
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white/90">Evolucao Mensal</h3>
              <p className="text-sm text-white/50">Lancamentos por vencimento nos ultimos 6 meses</p>
            </div>
            <div className="p-6">
              <GlassChart
                type="area"
                data={dashboard.monthly || []}
                dataKeys={chartKeys}
                colors={selectedBook === 'all' ? ['#22c55e', '#ef4444', '#6366f1'] : [BOOKS[selectedBook]?.color || '#6366f1']}
                height={320}
              />
            </div>
          </GlassCard>
        </FadeIn>

        <FadeIn delay={0.4}>
          <GlassCard className="h-full">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white/90">Alertas</h3>
              <p className="text-sm text-white/50">Pendencias vencidas ou proximas</p>
            </div>
            <div className="p-4 space-y-3">
              {dashboard.alertas?.length ? dashboard.alertas.map((alerta, index) => {
                const book = BOOKS[alerta.book] || BOOKS.geral;
                return (
                  <div key={`${alerta.message}-${index}`} className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                    <div className="flex items-start gap-3">
                      <AlertCircle className={`w-5 h-5 mt-0.5 ${alerta.type === 'danger' ? 'text-red-400' : 'text-yellow-400'}`} />
                      <div className="min-w-0">
                        <p className="text-sm text-white/80">{alerta.message}</p>
                        <p className="text-xs text-white/40 mt-1">
                          {book.name} - {formatBRL(alerta.valor)} - {formatDate(alerta.data_vencimento)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <p className="text-sm text-white/40 p-3">Nenhuma pendencia critica encontrada.</p>
              )}
            </div>
          </GlassCard>
        </FadeIn>
      </div>

      <FadeIn delay={0.5}>
        <GlassCard>
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white/90">Movimentacoes Recentes</h3>
              <p className="text-sm text-white/50">Ultimos lancamentos registrados</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/50">
              <Wallet className="w-4 h-4" />
              {dashboard.contas?.total || 0} contas
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {dashboard.recentes?.length ? dashboard.recentes.map((mov) => {
              const isReceita = mov.tipo === 'receita';
              return (
                <div key={mov.id} className="flex items-center justify-between gap-4 p-4 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isReceita ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      {isReceita ? <TrendingUp className="w-5 h-5 text-green-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-white/90 truncate">{mov.descricao || mov.pessoa_nome || mov.numero_documento || 'Lancamento'}</p>
                      <p className="text-sm text-white/50">{mov.categoria || mov.tipo} - {formatDate(mov.data_vencimento)}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-semibold ${isReceita ? 'text-green-400' : 'text-red-400'}`}>
                      {isReceita ? '+' : '-'}{formatBRL(mov.valor)}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${mov.status === 'pago' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {mov.status || 'pendente'}
                    </span>
                  </div>
                </div>
              );
            }) : (
              <p className="text-sm text-white/40 p-6">Nenhuma movimentacao registrada ainda.</p>
            )}
          </div>
        </GlassCard>
      </FadeIn>
    </div>
  );
};

export default FinanceiroDashboard;
